// src/pages/Shop.jsx
/* eslint-disable jsx-a11y/alt-text */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import SkeletonLoader from "../SkeletonLoader";
import ProductCard from "../ProductCard";

const CAT_CACHE_KEY = "all_categories";
const PRODS_CACHE_KEY = "all_products_shop";
const CACHE_TTL = 5 * 60 * 1000;

const Shop = () => {
  const [categories, setCategories] = useState([]);
  const [catData, setCatData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCat, setSelectedCat] = useState("all");

  // -------- helpers ----------
  const fetchCategories = async () => {
    const cached = sessionStorage.getItem(CAT_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Date.now() - parsed.timestamp < CACHE_TTL) {
        setCategories(parsed.data || []);
        return parsed.data || [];
      }
    }
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/category/get`
      );
      const arr = (res.data?.statusCode === 1 ? res.data?.data : []) || [];
      setCategories(arr);
      sessionStorage.setItem(
        CAT_CACHE_KEY,
        JSON.stringify({ data: arr, timestamp: Date.now() })
      );
      return arr;
    } catch (e) {
      console.error("Category load error", e);
      setCategories([]);
      return [];
    }
  };

  const fetchProductsByCategory = async (categoryId) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/products/get?category=${categoryId}`
      );
      if (res.data?.statusCode === 1) return res.data.data || [];
    } catch (e) {
      console.error("Product load error", categoryId, e);
    }
    return [];
  };

  const fetchAllProductsSequential = async (cats) => {
    const cached = sessionStorage.getItem(PRODS_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Date.now() - parsed.timestamp < CACHE_TTL) {
        setCatData(parsed.products || []);
        setLoading(false);
        return;
      }
    }

    const out = [];
    for (let i = 0; i < cats.length; i++) {
      const c = cats[i];
      const products = await fetchProductsByCategory(c._id);
      out.push({ _id: c._id, name: c.name, products });
      setCatData([...out]);
    }
    sessionStorage.setItem(
      PRODS_CACHE_KEY,
      JSON.stringify({ products: out, timestamp: Date.now() })
    );
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      const cats = await fetchCategories();
      if (!mounted) return;
      await fetchAllProductsSequential(cats);
    })();
    return () => (mounted = false);
  }, []);

  const visibleProducts = useMemo(() => {
    if (selectedCat === "all") {
      return catData.flatMap((c) =>
        c.products.map((p) => ({ ...p, __cat: c }))
      );
    }
    const found = catData.find((c) => c._id === selectedCat);
    return found ? found.products.map((p) => ({ ...p, __cat: found })) : [];
  }, [catData, selectedCat]);

  return (
    <div className="category_block">
      <Container>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            justifyContent: "space-between",
            marginTop: 12,
          }}
        >
          <h3 style={{ margin: 0, fontWeight: 700 }}>Shop</h3>

          <Form.Select
            value={selectedCat}
            onChange={(e) => setSelectedCat(e.target.value)}
            style={{ maxWidth: 260, borderRadius: 10 }}
          >
            <option value="all">All Products</option>
            {categories.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name}
              </option>
            ))}
          </Form.Select>
        </div>

        {loading && visibleProducts.length === 0 ? (
          <Row xs={2} md={3} lg={6} className="g-1 g-lg-3 mt-3">
            {[...Array(12)].map((_, i) => (
              <Col key={i}>
                <SkeletonLoader />
              </Col>
            ))}
          </Row>
        ) : visibleProducts.length === 0 ? (
          <div style={{ padding: "24px 0", color: "#666" }}>
            No products found.
          </div>
        ) : (
          <Row xs={2} md={3} lg={6} className="g-1 g-lg-3 mt-3">
            {visibleProducts.map((item, idx) => (
              <Col key={item._id || idx}>
                <ProductCard item={item} index={idx} />
              </Col>
            ))}
          </Row>
        )}
      </Container>
    </div>
  );
};

export default Shop;
